import React, { useState } from 'react';
import { tenantService } from '../../../services/tenantService';
import { Button } from '../../../components/ui/Button';
import { Checkbox, TextField } from '../../../components/ui/Field';
import { Overlay } from '../../../components/ui/Overlay';

interface CreateTenantModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/** Slugs address a tenant on its platform subdomain, so they are constrained. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/**
 * Creating a tenant.
 *
 * Whitelabel is decided here because it changes what the tenant is sold: it
 * enables branding and custom domains, and commits the tenant to supplying its own
 * Twilio and speech credentials rather than using the platform's. It can be
 * changed later on the Settings tab.
 *
 * The tenant's first admin is created in the same step: without one nobody can log
 * in to the tenant, and the tenant listing only shows tenants that have an admin.
 */
const MIN_ADMIN_PASSWORD = 8;
const looksLikeEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const CreateTenantModal: React.FC<CreateTenantModalProps> = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [isWhitelabeled, setIsWhitelabeled] = useState(false);
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const effectiveSlug = slugEdited ? slug : slugify(name);
  const adminValid = looksLikeEmail(adminEmail) && adminPassword.length >= MIN_ADMIN_PASSWORD;
  const canSubmit = Boolean(name.trim() && effectiveSlug && adminValid);

  const submit = async () => {
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      await tenantService.create({
        name: name.trim(),
        slug: effectiveSlug,
        isWhitelabeled,
        admin: {
          email: adminEmail.trim(),
          password: adminPassword,
          firstName: adminFirstName.trim() || undefined,
          lastName: adminLastName.trim() || undefined,
        },
      });
      setName('');
      setSlug('');
      setSlugEdited(false);
      setIsWhitelabeled(false);
      setAdminFirstName('');
      setAdminLastName('');
      setAdminEmail('');
      setAdminPassword('');
      setShowPassword(false);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err?.details?.message || err?.details?.details?.[0]?.msg || err?.details?.error || 'Could not create the tenant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay tone="dim-40" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-semibold">Create tenant</h2>

        <div className="mt-5 space-y-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Name</label>
            <TextField
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Client College"
              className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Slug</label>
            <TextField
              type="text"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(slugify(e.target.value));
              }}
              placeholder="client-college"
              className="rounded-lg border px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-agentic-primary-blue"
            />
            <p className="text-xs text-gray-500">
              Addresses the tenant on its platform subdomain. Lowercase letters, digits and hyphens.
            </p>
          </div>

          <div className="rounded-xl border bg-gray-50 p-4">
            <label className="flex items-center gap-3">
              <Checkbox
               
                checked={isWhitelabeled}
                onChange={(e) => setIsWhitelabeled(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-gray-700">Whitelabel tenant</span>
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Enables branding and custom domains, and requires this tenant to supply its own
              Twilio and speech credentials. Leave off to use the platform's global configuration
              for everything. This can be changed later.
            </p>
          </div>

          <div className="space-y-3 rounded-xl border p-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">Tenant admin</p>
              <p className="mt-0.5 text-xs text-gray-500">
                The first person who can log in to this tenant. They can add other users later.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">First name</label>
                <TextField
                  type="text"
                  value={adminFirstName}
                  onChange={(e) => setAdminFirstName(e.target.value)}
                  autoComplete="off"
                  className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Last name</label>
                <TextField
                  type="text"
                  value={adminLastName}
                  onChange={(e) => setAdminLastName(e.target.value)}
                  autoComplete="off"
                  className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Email</label>
              <TextField
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@client.com"
                autoComplete="off"
                className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Password</label>
              <div className="flex gap-2">
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  autoComplete="new-password"
                  className="flex-1 rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
                />
                <Button
                  tone="custom"
                  onClick={() => setShowPassword((v) => !v)}
                  className="rounded-lg border px-3 py-2 text-sm"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                At least {MIN_ADMIN_PASSWORD} characters. Share it with the admin securely; they can change it after logging in.
              </p>
            </div>
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button tone="custom" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">
            Cancel
          </Button>
          <Button tone="primary"
            onClick={submit}
            disabled={saving || !canSubmit}
            className="rounded-lg px-5 py-2 text-sm disabled:opacity-50"
          >
            {saving ? 'Creating…' : 'Create tenant'}
          </Button>
        </div>
      </div>
    </Overlay>
  );
};

export default CreateTenantModal;
